import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import {Button} from 'primereact/button'
import {Divider} from 'primereact/divider'
import {Sidebar} from 'primereact/sidebar'
import {Tag} from 'primereact/tag'
import React, {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useRef,
} from 'react'
import {useUserHospital} from '../../../context/hospital/UserHospitalContext'
import {
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../../query-manager/QueryKeysManager'
import {QueryKeyTypes} from '../../../query-manager/QueryKeyTypes'
import {checkEmptyString} from '../../../utils/CommonUtils'
import AddressForm from '../../address/AddressForm'
import {useAuth} from '../../authentication/AuthProvider'
import {component} from '../../form/Constant'
import FormHeader from '../../form/FormHeader'
import {useHospitalCRUD} from '../../hospital/query-keys/HospitalQueryManager'
import ImageUploader from '../../image/ImageUploader'
import PageContentWrapper from '../../layout/PageContentWrapper'
import {severityNotification} from '../../notification/NotificationConstant'
import {showNotification} from '../../notification/NotificationService'
import StyledDropdown from '../components/StyledDropdown'
import StyledInput from '../components/StyledInput'
import StyledHospitalOptions from '../styled-options/StyledHospitalOptions'
import {hospitalKeys, hospitalList, toPostDataKeys} from './Constants'

const HospitalForm = forwardRef((props, ref) => {
    useSignals()

    useImperativeHandle(ref, () => ({
        showForm: handleShowForm,
        hideForm: handleHideForm,
    }))

    //#region Context
    const {user} = useAuth()
    const {updateHospitals} = useUserHospital()
    const queryClient = useQueryClient()
    //#endregion

    //#region State
    const visible = useSignal(false)
    const formData = useSignal({})

    const formRefs = useRef([])

    const handleShowForm = useCallback(data => {
        if (data && data?.id) {
            formData.value = {
                ...data,
            }

            displaySettingPreview.value = {
                displaySetting: data?.displaySetting,
                name: data?.name,
                logo: data?.logo,
            }
        }

        visible.value = true
    }, [])

    const handleHideForm = useCallback(() => {
        displaySettingPreview.value = {
            displaySetting: 'textAndImage',
            name: '',
            logo: '',
        }
        formData.value = {}

        visible.value = false
    }, [])
    //#endregion

    //#region CRUD  Function
    const {mutateAsync: createOrUpdateHospital} = useHospitalCRUD(
        !!formData.value?.id ? QueryCRUDTypes.update : QueryCRUDTypes.create,
        user?.id,
    )

    const handleSubmit = async () => {
        let toPostData = {
            ...formData.value,
        }

        toPostDataKeys?.map(key => {
            if (key === hospitalKeys.address) {
                toPostData = {
                    ...toPostData,
                    ...formRefs.current[key]?.getValue(),
                }
            } else {
                toPostData[key] = formRefs.current[key]?.getValue()
            }
        })

        if (!toPostData?.hospitalNumber) {
            showNotification(
                severityNotification.error,
                'Hospital Number is required !!',
            )
            return
        }

        if (
            !toPostData?.name ||
            toPostData.name?.length < 0 ||
            !!checkEmptyString(toPostData?.name)
        ) {
            showNotification(
                severityNotification.error,
                'Hospital Name is required !!',
            )
            return
        }

        const hospitalData = await createOrUpdateHospital(toPostData)

        if (!!hospitalData?.id) {
            const {hospitalKeys} = QueryKeyManager(
                QueryKeyTypes.hospital,
                user?.id,
            )

            queryClient.invalidateQueries({
                queryKey: hospitalKeys.pattern(),
            })

            updateHospitals(hospitalData)

            handleHideForm()
        }
    }
    //#endregion

    //#region Handle Change Display Setting
    const displaySettingPreview = useSignal({
        displaySetting: 'textAndImage',
        name: '',
        logo: '',
    })

    const handleOnChangeDisplaySetting = (key, value) => {
        displaySettingPreview.value = {
            ...displaySettingPreview.value,
            [key]: value,
        }

        if (key === hospitalKeys.logo) {
            formData.value = {
                ...formData.value,
                logo: value,
            }
        }

        if (key === hospitalKeys.name) {
            displaySettingPreview.value = {
                ...displaySettingPreview.value,
                name: value,
            }
        }

        if (key === hospitalKeys.displaySetting) {
            displaySettingPreview.value = {
                ...displaySettingPreview.value,
                displaySetting: value,
            }
        }
    }
    //#endregion

    return (
        <Sidebar
            id="blockscroll-sidebar"
            className="p-sidebar-full"
            visible={visible.value}
            onHide={handleHideForm}
            blockScroll
            fullScreen>
            <PageContentWrapper>
                <FormHeader
                    // header={translateDatatable('HOSPITAL FORM')}
                    header="HOSPITAL FORM"
                    onHide={handleHideForm}
                />
                <React.Fragment>
                    <div className="grid">
                        {hospitalList?.map((item, index) => (
                            <React.Fragment key={item?.key}>
                                {/* Group Header */}
                                <div className="col-4 relative">
                                    <div className="sticky">
                                        <h4 className="font-bold underline-60">
                                            {item.label}
                                        </h4>
                                        <p>{item?.description}</p>
                                    </div>
                                </div>
                                {/* End region */}
                                {item?.children?.length > 0 && (
                                    <div className="col-8">
                                        {item?.children?.map(child => (
                                            <div
                                                key={child?.key}
                                                className="mb-3">
                                                {/* Component Header */}
                                                <div className="flex align-items-center justify-content-between">
                                                    <div>
                                                        <h5 className="font-bold">
                                                            {child?.label}
                                                        </h5>
                                                    </div>

                                                    <div>
                                                        {child?.required && (
                                                            <Tag
                                                                value={
                                                                    'This field is required !'
                                                                }
                                                                severity="danger"
                                                                icon="pi pi-info-circle"></Tag>
                                                        )}
                                                    </div>
                                                </div>
                                                {/* End Region */}

                                                {/* Render Child Components */}
                                                {child?.component ===
                                                    component.hospitalOptions && (
                                                    <StyledHospitalOptions
                                                        ref={el =>
                                                            (formRefs.current[
                                                                child.key
                                                            ] = el)
                                                        }
                                                        optionType={
                                                            component.dropdown
                                                        }
                                                        groupByIsNull={true}
                                                        placeholder={
                                                            child?.placeholder
                                                        }
                                                        value={
                                                            formData.value[
                                                                child?.key
                                                            ]
                                                        }
                                                        excludeGroupById={
                                                            !formData.value[
                                                                'groupBy'
                                                            ]
                                                                ? formData
                                                                      .value[
                                                                      'id'
                                                                  ]
                                                                : null
                                                        }
                                                    />
                                                )}

                                                {child?.component ===
                                                    component.inputText && (
                                                    <StyledInput
                                                        ref={el =>
                                                            (formRefs.current[
                                                                child.key
                                                            ] = el)
                                                        }
                                                        value={
                                                            formData.value[
                                                                child?.key
                                                            ] || ''
                                                        }
                                                        placeholder={
                                                            child?.placeholder
                                                        }
                                                        onChange={newValue => {
                                                            if (
                                                                child?.key ===
                                                                hospitalKeys.name
                                                            ) {
                                                                handleOnChangeDisplaySetting(
                                                                    hospitalKeys.name,
                                                                    newValue,
                                                                )
                                                            }
                                                        }}
                                                    />
                                                )}

                                                {child?.component ===
                                                    component.fileUpload && (
                                                    <>
                                                        <ImageUploader
                                                            id={child.key}
                                                            ref={el =>
                                                                (formRefs.current[
                                                                    child.key
                                                                ] = el)
                                                            }
                                                            initialImage={
                                                                formData.value[
                                                                    child?.key
                                                                ]
                                                            }
                                                            callBack={(
                                                                key,
                                                                newValue,
                                                            ) => {
                                                                if (
                                                                    key ===
                                                                    hospitalKeys.logo
                                                                ) {
                                                                    handleOnChangeDisplaySetting(
                                                                        hospitalKeys.logo,
                                                                        newValue,
                                                                    )
                                                                }
                                                            }}
                                                            multiple={
                                                                !!item?.multiple
                                                            }
                                                            extraData={
                                                                !!item?.extraData
                                                            }
                                                        />
                                                        The standard size of the
                                                        image is <b>120px</b> x
                                                        <b> 54px</b>.
                                                    </>
                                                )}

                                                {child?.component ===
                                                    component.dropdown && (
                                                    <React.Fragment>
                                                        <StyledDropdown
                                                            ref={el =>
                                                                (formRefs.current[
                                                                    child.key
                                                                ] = el)
                                                            }
                                                            value={
                                                                formData.value[
                                                                    child?.key
                                                                ] ||
                                                                child?.defaultValue
                                                            }
                                                            options={
                                                                child?.options ||
                                                                []
                                                            }
                                                            onChange={newValue => {
                                                                if (
                                                                    child?.key ===
                                                                    hospitalKeys.displaySetting
                                                                ) {
                                                                    handleOnChangeDisplaySetting(
                                                                        hospitalKeys.displaySetting,
                                                                        newValue,
                                                                    )
                                                                }
                                                            }}
                                                        />
                                                    </React.Fragment>
                                                )}

                                                {child?.component ===
                                                    component.address && (
                                                    <AddressForm
                                                        id={item.key}
                                                        ref={el =>
                                                            (formRefs.current[
                                                                child.key
                                                            ] = el)
                                                        }
                                                        className={'grid'}
                                                        childrenClassName={
                                                            'col-6'
                                                        }
                                                        defaultValue={
                                                            formData.value
                                                                ? {
                                                                      ...formData.value,
                                                                  }
                                                                : {}
                                                        }
                                                    />
                                                )}

                                                {/* LOGO SETTING PREVIEW */}
                                                {child?.key ===
                                                    hospitalKeys?.preview && (
                                                    <React.Fragment>
                                                        {displaySettingPreview
                                                            .value
                                                            ?.displaySetting ===
                                                        'textAndImage' ? (
                                                            !!displaySettingPreview
                                                                .value?.logo ||
                                                            displaySettingPreview
                                                                .value?.name ? (
                                                                <div className="flex align-items-center">
                                                                    {displaySettingPreview
                                                                        .value
                                                                        ?.logo && (
                                                                        <div>
                                                                            <img
                                                                                src={
                                                                                    displaySettingPreview
                                                                                        .value
                                                                                        ?.logo
                                                                                }
                                                                                alt="Uploaded Hospital Logo"
                                                                                style={{
                                                                                    height: 40,
                                                                                    width: 80,
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                    {displaySettingPreview
                                                                        .value
                                                                        ?.name && (
                                                                        <div>
                                                                            <h4 className="font-bold capitalized">
                                                                                {
                                                                                    displaySettingPreview
                                                                                        .value
                                                                                        ?.name
                                                                                }
                                                                            </h4>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <div>
                                                                    <i className="pi pi-exclamation-triangle mr-2"></i>
                                                                    <span>
                                                                        No
                                                                        information
                                                                        to
                                                                        preview
                                                                    </span>
                                                                </div>
                                                            )
                                                        ) : null}

                                                        {displaySettingPreview
                                                            .value
                                                            ?.displaySetting ===
                                                        'textOnly' ? (
                                                            displaySettingPreview
                                                                .value?.name ? (
                                                                <div className="flex align-items-center">
                                                                    {displaySettingPreview
                                                                        .value
                                                                        ?.name && (
                                                                        <div>
                                                                            <h4 className="font-bold capitalized mb-1">
                                                                                {
                                                                                    displaySettingPreview
                                                                                        .value
                                                                                        ?.name
                                                                                }
                                                                            </h4>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <div>
                                                                    <i className="pi pi-exclamation-triangle mr-2"></i>
                                                                    <span>
                                                                        No
                                                                        information
                                                                        to
                                                                        preview
                                                                    </span>
                                                                </div>
                                                            )
                                                        ) : null}

                                                        {displaySettingPreview
                                                            .value
                                                            ?.displaySetting ===
                                                        'imageOnly' ? (
                                                            !!displaySettingPreview
                                                                .value?.logo ? (
                                                                <div className="flex align-items-center">
                                                                    {displaySettingPreview
                                                                        .value
                                                                        ?.logo && (
                                                                        <div>
                                                                            <img
                                                                                src={
                                                                                    displaySettingPreview
                                                                                        .value
                                                                                        ?.logo
                                                                                }
                                                                                alt="Uploaded Hospital Logo"
                                                                                style={{
                                                                                    height: 40,
                                                                                    width: 80,
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <div>
                                                                    <i className="pi pi-exclamation-triangle mr-2"></i>
                                                                    <span>
                                                                        No
                                                                        information
                                                                        to
                                                                        preview
                                                                    </span>
                                                                </div>
                                                            )
                                                        ) : null}
                                                    </React.Fragment>
                                                )}
                                                {/* End Region */}

                                                {/* DESCRIPTION */}
                                                {child?.description &&
                                                    (typeof child?.description ===
                                                    'string' ? (
                                                        <p>
                                                            {child?.description}
                                                        </p>
                                                    ) : (
                                                        child?.description
                                                    ))}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <Divider type="dashed" />
                            </React.Fragment>
                        ))}
                    </div>
                </React.Fragment>

                {/* Action Button */}
                <Button
                    className="col-12 h-3rem"
                    label="SUBMIT"
                    icon="pi pi-send"
                    onClick={() => handleSubmit()}
                />
            </PageContentWrapper>
        </Sidebar>
    )
})

export default HospitalForm
